import {
  type PathLookupEventTopic,
  type PathLookupGlobalEvent,
  pathLookupEventTopics,
} from '@ipfabric/utils';
import { nullable } from '@ipfabric/utils/fp/nullable';
import { prop } from '@ipfabric/utils/fp/prop';
import classNames from 'classnames';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import { GraphSidePanelNoData } from 'components/Graph/GraphSidePanel/GraphSidePanelNoData';
import { isPathLookupGraphMetadata } from 'components/Graph/GraphState';
import type { ShowSidePanelCommand } from 'components/Graph/commands/showSidePanel';
import { SeverityCountsListItem } from 'components/SeverityCounts/SeverityCountsListItem';
import { SlidePaneClose } from 'components/SlidePane/SlidePaneClose';
import { useSlidePane } from 'components/SlidePane/useSlidePane';
import { SeverityIcon } from 'components/icons/SeverityIcon';
import { useLocale } from 'containers/App/LocaleContext';
import { flow } from 'fp-ts/lib/function';
import { type FC, memo } from 'react';

const pathLookupDataInputTab: ShowSidePanelCommand = {
  pathLookupTabId: 'dataInput',
};

const BarePathLookupResultDetails: FC = () => {
  const msgPathLookup = useLocale().canvasGraph.graphSidePanel.pathlookup;
  const ctxProps = useGraphContextSelectors({
    eventsSummary: flow(
      prop('graphMetadata'),
      nullable.fromPredicate(isPathLookupGraphMetadata),
      nullable.chain(prop('eventsSummary')),
    ),
    ignoredTopics: prop('pathlookup', 'preset', 'pathLookup', 'ignoredTopics'),
  });

  const slidePane = useSlidePane();

  const renderGlobalEvent = (event: PathLookupGlobalEvent, index: number) => (
    <p key={index}>
      <SeverityIcon className='mr-lgu-2' severity={event.severity} />
      {msgPathLookup.resultDetails.globalEvents[event.name]}
      {event.details && (
        <>
          {' '}
          <ButtonAsA
            onClick={() => {
              slidePane.openPane(
                <>
                  <SlidePaneClose
                    className='background-lightgray-e px-5'
                    onClose={slidePane.closePane}
                    label={msgPathLookup.resultDetails.slidePaneClose}
                  />
                  <div className='p-5'>
                    {event.details?.map((detail, detailIndex) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: no better key
                      <p key={detailIndex}>{detail}</p>
                    ))}
                  </div>
                </>,
              );
            }}
          >
            {msgPathLookup.resultDetails.details}
          </ButtonAsA>
        </>
      )}
    </p>
  );

  const renderTopicSummary = (topic: PathLookupEventTopic) => (
    <SeverityCountsListItem
      key={topic}
      name={
        <span
          className={classNames(
            'font-bold',
            ctxProps.ignoredTopics.includes(topic) && 'text-muted line-through',
          )}
        >
          {msgPathLookup.pathLookupEventTopics[topic]}
        </span>
      }
      counts={ctxProps.eventsSummary!.topics[topic]}
    />
  );

  return (
    <div className='relative flex flex-1 flex-col p-5'>
      {slidePane.pane}
      {ctxProps.eventsSummary ? (
        <>
          {ctxProps.eventsSummary.global.length > 0 && (
            <div className='mb-10'>
              {ctxProps.eventsSummary.global.map(renderGlobalEvent)}
            </div>
          )}
          <div className='mb-10'>
            {pathLookupEventTopics.map(renderTopicSummary)}
          </div>
        </>
      ) : (
        <GraphSidePanelNoData panelTabToLoadData={pathLookupDataInputTab} />
      )}
    </div>
  );
};

export const PathLookupResultDetails = memo(BarePathLookupResultDetails);
